// Security (SM-968): the signed-in staff member's own sign-in — change the
// password (every other device is signed out), set up an authenticator app
// (scan the QR code, confirm with a 6-digit code) or remove it (a current
// code is needed, so a session alone cannot turn 2FA off). A user-level
// screen like Invitations: it needs no capability on the selected node.
//
//   GET  /api/broker/auth/me
//   POST /api/broker/auth/password/change    {current_password, new_password}
//   POST /api/broker/auth/mfa/totp           → {secret, otpauth_url, …}
//   POST /api/broker/auth/mfa/totp/confirm   {code}
//   POST /api/broker/auth/mfa/totp/remove    {code}
//
// Under FF_BROKER_STAFF_IDENTITY=client the portal signs in with the
// trading-site account, whose security settings live there; the page says
// so instead.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { KeyValues, Time } from "@/components/ui/Common";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { PASSWORD_MIN } from "@/components/auth/PasswordLinkPage";
import { APIError } from "@/lib/api";
import { changePassword, confirmTOTP, enrollTOTP, fetchStaffAccount, identityMode, removeTOTP } from "@/lib/auth";
import type { StaffTOTPEnrollResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/security")({
  component: SecurityPage,
});

const accountKey = ["auth", "staff-account"] as const;

function SecurityPage() {
  const { t } = useI18n();
  const mode = useQuery({ queryKey: ["identity-mode"], queryFn: identityMode, staleTime: Infinity });
  const account = useQuery({ queryKey: accountKey, queryFn: fetchStaffAccount, enabled: mode.data === "staff" });

  return (
    <>
      <PageHeader title={t("security.title")} subtitle={t("security.subtitle")} coverage="none" />
      {mode.data === "client" ? (
        <p className="banner text-xs">{t("security.clientMode")}</p>
      ) : account.isPending ? (
        <LoadingState />
      ) : account.isError ? (
        <ErrorState error={account.error} onRetry={() => void account.refetch()} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard title={t("security.account")}>
            <KeyValues
              rows={[
                { label: t("security.name"), value: account.data.display_name || "—" },
                { label: t("login.email"), value: account.data.email },
                {
                  label: t("security.passwordChanged"),
                  value: account.data.password_changed_at_unix_ms ? <Time ms={account.data.password_changed_at_unix_ms} /> : "—",
                },
              ]}
            />
          </SectionCard>
          <ChangePassword />
          <Authenticator enrolled={account.data.totp_enrolled} />
        </div>
      )}
    </>
  );
}

function refusal(e: unknown, fallback: string): string {
  if (e instanceof APIError && e.status === 400 && e.message && !/^HTTP \d+$/.test(e.message)) return e.message;
  return fallback;
}

function ChangePassword() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const short = next.length > 0 && next.length < PASSWORD_MIN;
  const mismatch = confirm.length > 0 && confirm !== next;
  const canSubmit = current !== "" && next.length >= PASSWORD_MIN && confirm === next;
  const mut = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      void qc.invalidateQueries({ queryKey: accountKey });
    },
  });
  return (
    <SectionCard title={t("security.changePassword")}>
      {done && (
        <div className="banner mb-3 text-xs" role="status">
          <span aria-hidden>✓</span>
          <span>{t("security.passwordChangedDone")}</span>
        </div>
      )}
      {mut.isError && (
        <div className="banner danger mb-3 text-xs" role="alert">
          <span aria-hidden>⚠</span>
          <span>{refusal(mut.error, t("password.error.failed"))}</span>
        </div>
      )}
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setDone(false);
          if (canSubmit) mut.mutate();
        }}
      >
        <Input
          label={t("security.currentPassword")}
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Input
          label={t("password.new")}
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          required
          hint={t("password.rule", { min: PASSWORD_MIN })}
          error={short ? t("password.tooShort", { min: PASSWORD_MIN }) : null}
        />
        <Input
          label={t("password.confirm")}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
          error={mismatch ? t("password.mismatch") : null}
        />
        <p className="text-xs text-t-3">{t("security.changePasswordHint")}</p>
        <Button type="submit" loading={mut.isPending} disabled={!canSubmit}>
          {t("security.changePassword")}
        </Button>
      </form>
    </SectionCard>
  );
}

function Authenticator({ enrolled }: { enrolled: boolean }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [setup, setSetup] = useState<StaffTOTPEnrollResponse | null>(null);
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const after = (msg: string) => {
    setSetup(null);
    setCode("");
    setNotice(msg);
    void qc.invalidateQueries({ queryKey: accountKey });
  };
  const enroll = useMutation({ mutationFn: enrollTOTP, onSuccess: (r) => setSetup(r) });
  const confirm = useMutation({ mutationFn: () => confirmTOTP(code), onSuccess: () => after(t("security.totpOnDone")) });
  const remove = useMutation({ mutationFn: () => removeTOTP(code), onSuccess: () => after(t("security.totpOffDone")) });
  const failed = enroll.error ?? confirm.error ?? remove.error;
  const codeOk = /^\d{6}$/.test(code);

  return (
    <SectionCard
      title={t("security.authenticator")}
      actions={<Badge tone={enrolled ? "yes" : "neutral"}>{enrolled ? t("security.totpOn") : t("security.totpOff")}</Badge>}
      className="lg:col-span-2"
    >
      {notice && (
        <div className="banner mb-3 text-xs" role="status">
          <span aria-hidden>✓</span>
          <span>{notice}</span>
        </div>
      )}
      {failed && (
        <div className="banner danger mb-3 text-xs" role="alert">
          <span aria-hidden>⚠</span>
          <span>{refusal(failed, t("security.totpFailed"))}</span>
        </div>
      )}
      {enrolled ? (
        <form
          className="max-w-sm space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (codeOk) remove.mutate();
          }}
        >
          <p className="text-sm text-t-2">{t("security.totpOnBody")}</p>
          <CodeInput value={code} onChange={setCode} />
          <Button type="submit" variant="danger" loading={remove.isPending} disabled={!codeOk}>
            {t("security.totpRemove")}
          </Button>
        </form>
      ) : setup ? (
        <div className="grid gap-4 sm:grid-cols-[auto,1fr]">
          <div className="w-fit rounded-lg bg-white p-3" aria-label={t("security.qrAria")}>
            <QRCodeSVG value={setup.otpauth_url} size={176} level="M" />
          </div>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (codeOk) confirm.mutate();
            }}
          >
            <ol className="list-decimal space-y-1 pl-4 text-sm text-t-2">
              <li>{t("security.totpStep1")}</li>
              <li>{t("security.totpStep2")}</li>
            </ol>
            <div className="text-xs text-t-3">
              {t("security.totpManual")}{" "}
              <code className="mono break-all rounded bg-bg-3 px-1.5 py-0.5 text-t-1">{setup.secret}</code>
            </div>
            <CodeInput value={code} onChange={setCode} />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" loading={confirm.isPending} disabled={!codeOk}>
                {t("security.totpConfirm")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setSetup(null)}>
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="max-w-xl space-y-3">
          <p className="text-sm text-t-2">{t("security.totpOffBody")}</p>
          <Button
            onClick={() => {
              setNotice(null);
              enroll.mutate();
            }}
            loading={enroll.isPending}
          >
            {t("security.totpSetUp")}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  return (
    <Input
      label={t("security.code")}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="123456"
      required
    />
  );
}
